import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import DataTable from '../../components/DataTable/DataTable';
import userRepository from '../../services/repositories/user.repository';
import { buildImageUrl } from '../../utils/imageUrl';
import { Button, ButtonGroup } from 'react-bootstrap';
import '../../styles/products.scss';

const CustomerIndex = () => {
  const { setTitle } = useTitle();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'clients', 'equipe'

  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userRepository.getAll();
      const customersData = response.data || response;
      
      const formattedCustomers = Array.isArray(customersData) ? customersData.map(customer => {
        const imageUrl = buildImageUrl(customer.image);
        
        return {
          ...customer,
          image: imageUrl ? [imageUrl] : [],
        };
      }) : [];
      
      setCustomers(formattedCustomers);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err.message || 'Erreur lors du chargement des clients');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter customers based on selected filter
  const filteredCustomers = useMemo(() => {
    if (filter === 'all') return customers;
    if (filter === 'clients') return customers.filter(customer => customer.role === 'client');
    if (filter === 'equipe') return customers.filter(customer => customer.role === 'manager' || customer.role === 'distributeur');
    return customers;
  }, [customers, filter]);

  const columns = useMemo(() => [
    {
      header: 'Image',
      accessor: 'image',
      type: 'image',
      sortable: false,
    },
    {
      header: 'Nom',
      accessor: 'name',
      sortable: true,
      render: (value, row) => `${value} ${row.prenom || ''}`,
    },
    {
      header: 'Email',
      accessor: 'email',
      sortable: true,
    },
    {
      header: 'Téléphone',
      accessor: 'phone',
      sortable: false,
      render: (value) => value || '-',
    },
    {
      header: 'Rôle',
      accessor: 'role',
      sortable: true,
      render: (value) => (
        <span className={`badge ${value === 'client' ? 'bg-primary' : 'bg-success'}`}>
          {value === 'client' ? 'Client' : value === 'manager' ? 'Manager' : 'Distributeur'}
        </span>
      ),
    },
    {
      header: 'Adresse',
      accessor: 'address',
      sortable: false,
      render: (value) => value || '-',
    },
    {
      header: 'Salaire',
      accessor: 'salaire',
      sortable: true,
      render: (value) => value ? `${value} DH` : '-',
    },
  ], []);

  const handleAdd = useCallback(() => {
    navigate('/admin/customer/customer-create');
  }, [navigate]);

  const handleEdit = useCallback((customer) => {
    navigate(`/admin/customer/customer-edit/${customer.id}`);
  }, [navigate]);

  const handleDelete = useCallback(async (customer) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      try {
        setLoading(true);
        await userRepository.delete(customer.id);
        await fetchCustomers();
      } catch (err) {
        console.error('Error deleting customer:', err);
        alert('Erreur lors de la suppression du client');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchCustomers]);

  const handleBulkDelete = useCallback(async (selectedIds) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedIds.length} clients ?`)) {
      try {
        setLoading(true);
        for (const id of selectedIds) {
          await userRepository.delete(id);
        }
        alert(`${selectedIds.length} clients supprimés avec succès`);
        await fetchCustomers();
      } catch (error) {
        console.error('Bulk delete error:', error);
        alert('Erreur lors de la suppression des clients');
      } finally {
        setLoading(false);
      }
    }
  }, [fetchCustomers]);

  useEffect(() => {
    const titles = {
      all: 'Liste des Clients',
      clients: 'Liste des Clients',
      equipe: 'Liste de l\'Équipe'
    };
    setTitle(titles[filter]);
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <div className="customer-list-page">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      
      {/* Filter Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <ButtonGroup className="mb-3">
          <Button
            variant={filter === 'all' ? 'primary' : 'outline-primary'}
            onClick={() => setFilter('all')}
            className="rounded-start"
          >
            Tous ({customers.length})
          </Button>
          <Button
            variant={filter === 'clients' ? 'primary' : 'outline-primary'}
            onClick={() => setFilter('clients')}
          >
            Clients ({customers.filter(c => c.role === 'client').length})
          </Button>
          <Button
            variant={filter === 'equipe' ? 'primary' : 'outline-primary'}
            onClick={() => setFilter('equipe')}
            className="rounded-end"
          >
            Équipe ({customers.filter(c => c.role === 'manager' || c.role === 'distributeur').length})
          </Button>
        </ButtonGroup>
      </div>
      
      <DataTable
        data={filteredCustomers}
        columns={columns}
        loading={loading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onBulkAction={handleBulkDelete}
        title={`Liste des ${filter === 'all' ? 'Clients' : filter === 'clients' ? 'Clients' : 'Équipe'}`}
        addButtonText={filter === 'equipe' ? 'Nouveau Membre d\'Équipe' : 'Nouveau Client'}
        searchPlaceholder={`Rechercher des ${filter === 'all' ? 'clients' : filter === 'clients' ? 'clients' : 'membres d\'équipe'}...`}
        pageSize={10}
        selectable={true}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
};

export default CustomerIndex;